// src/pages/UserCreditsPage.jsx — UI Pro Moderne v2
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Stack, Typography, Chip, Alert, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, TablePagination,
  IconButton, Tooltip, LinearProgress, Divider,
} from "@mui/material";
import RefreshIcon              from "@mui/icons-material/Refresh";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingDownIcon         from "@mui/icons-material/TrendingDown";
import TrendingUpIcon           from "@mui/icons-material/TrendingUp";
import TimerIcon                from "@mui/icons-material/Timer";
import WarningAmberIcon         from "@mui/icons-material/WarningAmber";
import { useTranslation } from "react-i18next";
import api, { getUserMessage } from "../api/axios";
import { pageWrap, minutesFmt } from "../utils/pageStyles";

// ── Helpers ────────────────────────────────────────────────────────────────
const TYPE_META = {
  reservation_debit:  { label: "Débit",         color: "#dc2626", bg: "#fef2f2", sign: -1 },
  reservation_refund: { label: "Remboursement",  color: "#16a34a", bg: "#f0fdf4", sign: +1 },
  admin_grant:        { label: "Attribution",    color: "#7c3aed", bg: "#f5f3ff", sign: +1 },
  course_package:     { label: "Pack cours",     color: "#0369a1", bg: "#f0f9ff", sign: +1 },
  manual_adjustment:  { label: "Ajustement",     color: "#b45309", bg: "#fffbeb", sign: +1 },
};

function getTypeMeta(type) {
  return TYPE_META[type] || { label: type || "—", color: "#64748b", bg: "#f8fafc", sign: 0 };
}

function fmtDelta(val) {
  const n = Number(val || 0);
  if (n === 0) return "0 min";
  return `${n > 0 ? "+" : ""}${Math.abs(n)} min`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("fr", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

// ── KPI card ───────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, icon, color, bg, progress, urgent }) {
  return (
    <Paper elevation={0} sx={{
      flex: 1, minWidth: 160, p: 2.5, borderRadius: 3,
      border: "1.5px solid",
      borderColor: urgent ? "#fca5a5" : "divider",
      bgcolor: urgent ? "#fff5f5" : "background.paper",
      position: "relative", overflow: "hidden",
      transition: "box-shadow .2s",
      "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,.07)" },
    }}>
      {urgent && (
        <Box sx={{
          position: "absolute", top: 10, right: 10,
          animation: "blink 1.5s ease-in-out infinite",
          "@keyframes blink": { "0%,100%": { opacity: 1 }, "50%": { opacity: .3 } },
        }}>
          <WarningAmberIcon sx={{ fontSize: 14, color: "#ef4444" }} />
        </Box>
      )}
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{
          width: 40, height: 40, borderRadius: 2,
          bgcolor: bg, display: "flex",
          alignItems: "center", justifyContent: "center", color,
          flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" fontWeight={700} lineHeight={1}
            sx={{ color: urgent ? "#ef4444" : "text.primary" }}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: 12 }}>
            {label}
          </Typography>
          {sub && (
            <Typography variant="caption" sx={{ color, fontSize: 11 }}>{sub}</Typography>
          )}
        </Box>
      </Stack>
      {progress != null && (
        <Box sx={{ mt: 1.5 }}>
          <LinearProgress variant="determinate" value={progress}
            sx={{
              height: 5, borderRadius: 3, bgcolor: "#f1f5f9",
              "& .MuiLinearProgress-bar": {
                bgcolor: progress < 20 ? "#ef4444" : progress < 40 ? "#f59e0b" : color,
                borderRadius: 3,
              },
            }}
          />
        </Box>
      )}
    </Paper>
  );
}

// ── Wallet card ────────────────────────────────────────────────────────────
function WalletCard({ wallet }) {
  const now        = new Date();
  const expiresAt  = wallet.expires_at ? new Date(wallet.expires_at) : null;
  const isExpired  = expiresAt && expiresAt <= now;
  const isExhausted = (wallet.remaining_minutes || 0) <= 0;
  const isActive   = wallet.is_active && !isExpired && !isExhausted;

  const pct = wallet.total_minutes > 0
    ? Math.round((wallet.remaining_minutes / wallet.total_minutes) * 100)
    : 0;

  const barColor = isExpired || isExhausted ? "#ef4444"
    : pct > 50 ? "#22c55e"
    : pct > 20 ? "#f59e0b"
    : "#ef4444";

  const statusLabel = isExpired ? "Expiré"
    : isExhausted ? "Épuisé"
    : !wallet.is_active ? "Inactif"
    : "Actif";
  const statusColor = isActive ? "#16a34a" : "#dc2626";
  const statusBg    = isActive ? "#f0fdf4" : "#fef2f2";

  const daysLeft = expiresAt && !isExpired
    ? Math.ceil((expiresAt - now) / 86400000)
    : null;

  return (
    <Box sx={{
      p: 2, borderRadius: 2.5,
      border: "1px solid",
      borderColor: isActive ? "#e2e8f0" : "#fecaca",
      bgcolor: isActive ? "background.paper" : "#fff9f9",
      opacity: isActive ? 1 : 0.75,
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
            <Typography variant="body2" fontWeight={700} noWrap>
              {wallet.pack_code || `Wallet #${wallet.id}`}
            </Typography>
            <Chip size="small" label={statusLabel}
              sx={{ height: 18, fontSize: 10, fontWeight: 700,
                bgcolor: statusBg, color: statusColor }} />
            {daysLeft !== null && daysLeft <= 7 && (
              <Chip size="small" label={`⚠ ${daysLeft}j`}
                sx={{ height: 18, fontSize: 10, fontWeight: 700,
                  bgcolor: "#fffbeb", color: "#b45309" }} />
            )}
          </Stack>
          {wallet.pack_name && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: 11 }}>
              {wallet.pack_name}
            </Typography>
          )}
        </Box>
        <Box sx={{ textAlign: "right", flexShrink: 0, ml: 1 }}>
          <Typography variant="body2" fontWeight={700} sx={{ color: barColor }}>
            {wallet.remaining_minutes ?? "—"} min
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
            / {wallet.total_minutes} min
          </Typography>
        </Box>
      </Stack>

      <LinearProgress variant="determinate" value={isActive ? pct : 0}
        sx={{
          height: 6, borderRadius: 3, bgcolor: "#f1f5f9",
          "& .MuiLinearProgress-bar": { bgcolor: barColor, borderRadius: 3 },
        }}
      />

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          {pct}% restant · utilisé : {wallet.used_minutes ?? 0} min
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          {expiresAt
            ? (isExpired ? `Expiré le ${expiresAt.toLocaleDateString("fr")}`
               : `Expire le ${expiresAt.toLocaleDateString("fr")}`)
            : "Sans expiration"}
        </Typography>
      </Stack>
    </Box>
  );
}

// ── Page principale ────────────────────────────────────────────────────────
export default function UserCreditsPage() {
  const { t }  = useTranslation();
  const [loading,     setLoading]     = useState(false);
  const [summary,     setSummary]     = useState(null);
  const [wallets,     setWallets]     = useState([]);
  const [rows,        setRows]        = useState([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [error,       setError]       = useState("");

  const offset = useMemo(() => page * rowsPerPage, [page, rowsPerPage]);

  const fetchData = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/reservations/lab-credits/me", {
        params: { limit: rowsPerPage, offset },
      });
      setSummary(data.summary || null);
      setWallets(data.summary?.wallets || []);
      const tx = data.transactions || {};
      setRows(tx.items || []);
      setTotal(tx.total || 0);
    } catch (err) {
      setError(getUserMessage(err, t("common.unknownError")));
    } finally { setLoading(false); }
  }, [rowsPerPage, offset, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const remaining = summary?.remaining_minutes ?? 0;
  const used      = summary?.used_minutes      ?? 0;
  const totalMin  = summary?.total_credits     ?? (remaining + used);
  const pct       = totalMin > 0
    ? Math.min(100, Math.max(0, Math.round((remaining / totalMin) * 100)))
    : null;
  const isLow     = pct !== null && pct < 20;
  const activeWallets = wallets.filter(w => {
    const now = new Date();
    const ea  = w.expires_at ? new Date(w.expires_at) : null;
    return w.is_active && (!ea || ea > now) && (w.remaining_minutes || 0) > 0;
  });

  return (
    <Box sx={pageWrap}>

      {/* Header */}
      <Stack direction={{ xs:"column", sm:"row" }} alignItems={{ sm:"center" }}
        justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{t("credits.title")}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {t("credits.transactions")} — {total.toLocaleString()} {t("common.total")}
          </Typography>
        </Box>
        <Tooltip title={t("common.refresh")}>
          <IconButton onClick={fetchData} disabled={loading} size="small"
            sx={{ border:"1px solid", borderColor:"divider", borderRadius:2 }}>
            {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError("")}
          sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>
      )}

      {/* KPIs */}
      {summary && (
        <Stack direction={{ xs:"column", sm:"row" }} spacing={2} flexWrap="wrap" sx={{ mb: 3 }}>
          <KpiCard
            label={t("credits.balance")}
            value={`${remaining} min`}
            icon={<AccountBalanceWalletIcon />}
            color={isLow ? "#ef4444" : "#22c55e"}
            bg={isLow ? "#fef2f2" : "#f0fdf4"}
            progress={pct}
            urgent={isLow}
            sub={pct != null ? `${pct}% restant` : null}
          />
          <KpiCard
            label={t("credits.used")}
            value={`${used} min`}
            icon={<TrendingDownIcon />}
            color="#f59e0b" bg="#fffbeb"
            sub={totalMin > 0 ? `${Math.round((used / totalMin) * 100)}% consommé` : null}
          />
          <KpiCard
            label={t("credits.quota")}
            value={`${totalMin} min`}
            icon={<TimerIcon />}
            color="#6366f1" bg="#eef2ff"
            sub={`${summary?.total_wallets ?? 0} wallet${(summary?.total_wallets ?? 0) > 1 ? "s" : ""} · ${activeWallets.length} actif${activeWallets.length > 1 ? "s" : ""}`}
          />
          <KpiCard
            label="Transactions"
            value={total.toLocaleString("fr")}
            icon={<TrendingUpIcon />}
            color="#0369a1" bg="#f0f9ff"
          />
        </Stack>
      )}

      {/* Wallets */}
      {wallets.length > 0 && (
        <Paper elevation={0} sx={{
          border:"1px solid", borderColor:"divider",
          borderRadius:3, overflow:"hidden", mb:3,
        }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between"
            sx={{ px:3, py:2, borderBottom:"1px solid", borderColor:"divider" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AccountBalanceWalletIcon sx={{ fontSize:18, color:"text.secondary" }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Wallets ({wallets.length})
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {activeWallets.length} actif{activeWallets.length > 1 ? "s" : ""}
            </Typography>
          </Stack>
          <Box sx={{ p:2.5 }}>
            <Stack spacing={1.5}>
              {wallets.map(w => <WalletCard key={w.id} wallet={w} />)}
            </Stack>
          </Box>
        </Paper>
      )}

      {/* Tableau transactions */}
      <Paper elevation={0} sx={{
        border:"1px solid", borderColor:"divider",
        borderRadius:3, overflow:"hidden",
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between"
          sx={{ px:3, py:2, borderBottom:"1px solid", borderColor:"divider" }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t("credits.transactions")}
          </Typography>
          {loading && <CircularProgress size={14} />}
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor:"#f8fafc" }}>
              {["Date", "Type", "Variation", "Réservation", "Note"].map((h, i) => (
                <TableCell key={i} sx={{
                  fontWeight:700, fontSize:11, color:"text.secondary",
                  textTransform:"uppercase", letterSpacing:.4,
                  borderBottom:"1px solid #e2e8f0", py:1.5,
                }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center"
                  sx={{ py:6, color:"text.secondary" }}>
                  {t("credits.no_transactions")}
                </TableCell>
              </TableRow>
            ) : rows.map(tx => {
              const meta  = getTypeMeta(tx.type);
              const delta = Number(tx.change_minutes || tx.amount || 0);
              const isPos = delta > 0;
              const isNeg = delta < 0;
              return (
                <TableRow key={tx.id} sx={{
                  "&:hover": { bgcolor:"#f8fafc" },
                  "& td": { borderBottom:"1px solid #f1f5f9" },
                  "&:last-child td": { borderBottom:0 },
                }}>
                  <TableCell sx={{ fontSize:12, color:"text.secondary", whiteSpace:"nowrap" }}>
                    {fmtDate(tx.created_at)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{
                      display:"inline-flex", px:1.5, py:0.4, borderRadius:2,
                      bgcolor:meta.bg, color:meta.color,
                      fontSize:11, fontWeight:700, whiteSpace:"nowrap",
                    }}>
                      {meta.label}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {isPos && <TrendingUpIcon sx={{ fontSize:13, color:"#16a34a" }} />}
                      {isNeg && <TrendingDownIcon sx={{ fontSize:13, color:"#dc2626" }} />}
                      <Typography variant="caption" fontWeight={700} sx={{
                        color: isPos ? "#16a34a" : isNeg ? "#dc2626" : "text.secondary",
                        fontSize:13,
                      }}>
                        {fmtDelta(delta)}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize:12, color:"text.secondary" }}>
                    {tx.reservation_id
                      ? <Chip size="small" label={`#${tx.reservation_id}`}
                          sx={{ height:18, fontSize:10, fontWeight:600,
                            bgcolor:"#f1f5f9", color:"#475569" }} />
                      : "—"
                    }
                  </TableCell>
                  <TableCell sx={{ fontSize:12, color:"text.secondary", maxWidth:220 }}>
                    <Tooltip title={tx.description || tx.note || ""}>
                      <Typography variant="caption" noWrap sx={{ maxWidth:200, display:"block" }}>
                        {tx.note || tx.description || "—"}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage={t("common.per_page")}
          sx={{ borderTop:"1px solid", borderColor:"divider" }}
        />
      </Paper>
    </Box>
  );
}
